import type { ComponentProps } from 'react';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface DateRangePickerProps {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  /** Cantidad de meses visibles en el calendario (default 2). */
  numberOfMonths?: number;
  align?: 'start' | 'center' | 'end';
  /** Deshabilita fechas futuras y/o muy antiguas. */
  disabled?: ComponentProps<typeof Calendar>['disabled'];
}

/**
 * Date range picker de shadcn (Popover + Button + Calendar en `mode="range"`).
 * Reemplaza los `<input type="date">` nativos — consistente con el resto de
 * la UI (regla: SIEMPRE componentes shadcn).
 */
export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Rango de fechas',
  className,
  numberOfMonths = 2,
  align = 'start',
  disabled,
}: DateRangePickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            // Full-width en mobile por defecto; ancho natural en sm+.
            'w-full justify-start gap-2 font-normal sm:w-auto',
            !value?.from && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="size-4 shrink-0" />
          {value?.from ? (
            value.to ? (
              <span className="truncate">
                {format(value.from, 'dd MMM yyyy', { locale: es })} –{' '}
                {format(value.to, 'dd MMM yyyy', { locale: es })}
              </span>
            ) : (
              <span className="truncate">{format(value.from, 'dd MMM yyyy', { locale: es })}</span>
            )
          ) : (
            <span className="truncate">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="range"
          defaultMonth={value?.from}
          selected={value}
          onSelect={onChange}
          numberOfMonths={numberOfMonths}
          disabled={disabled}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
